"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import { Heart, Star, EyeOff } from "lucide-react";
import { logger } from "@/lib/logger";
import { useAuth } from "@/components/auth-provider";

// Add LogoGridSkeleton component
function LogoGridSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-4 md:gap-x-6 md:gap-y-10">
      {[...Array(8)].map((_, index) => (
        <div key={index}>
          <div className="aspect-square animate-pulse rounded-lg border border-line bg-surface-2" />
          <div className="flex items-center gap-2.5 pt-4">
            <div className="size-8 shrink-0 animate-pulse rounded-full bg-surface-2" />
            <div className="min-w-0 flex-1 space-y-1.5">
              <div className="h-3 w-24 animate-pulse rounded-sm bg-surface-2" />
              <div className="h-2.5 w-16 animate-pulse rounded-sm bg-surface-2" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Author avatar component
function AuthorAvatar({ name, photoURL }: { name: string; photoURL?: string }) {
  const [imageError, setImageError] = useState(false);

  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  // Show initials if no photoURL or if image failed to load
  if (!photoURL || imageError) {
    return (
      <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-surface-2 text-micro font-medium text-ink-muted ring-1 ring-line ring-offset-2 ring-offset-paper">
        {initials || "?"}
      </div>
    );
  }

  return (
    <Image
      src={photoURL}
      alt={name}
      width={32}
      height={32}
      className="size-8 shrink-0 rounded-full object-cover ring-1 ring-line ring-offset-2 ring-offset-paper"
      unoptimized
      onError={() => setImageError(true)}
    />
  );
}

interface Logo {
  id: string;
  imageUrl: string;
  createdAt: any;
  authorId: string;
  authorName: string;
  authorPhotoURL: string;
  brandName: string;
  likes: number;
  hasLiked: boolean;
  isFeatured: boolean;
  isHidden: boolean;
}

interface ApiResponse {
  logos: Logo[];
  nextCursor: string | null;
  error?: string;
}

interface CommunityLogoGridProps {
  hideShowMoreButton?: boolean;
  isAdmin?: boolean;
}

export default function CommunityLogoGrid({ hideShowMoreButton = false, isAdmin = false }: CommunityLogoGridProps) {
  const { user, loading: authLoading } = useAuth();
  const userId = user?.id ?? null;
  const [logos, setLogos] = useState<Logo[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [likingIds, setLikingIds] = useState<Set<string>>(new Set());
  const [featuringIds, setFeaturingIds] = useState<Set<string>>(new Set());
  const [hidingIds, setHidingIds] = useState<Set<string>>(new Set());
  const [visitorId, setVisitorId] = useState<string | null>(null);

  // Generate or retrieve visitor ID for anonymous likes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      let storedVisitorId = localStorage.getItem('communityVisitorId');
      if (!storedVisitorId) {
        storedVisitorId = crypto.randomUUID();
        localStorage.setItem('communityVisitorId', storedVisitorId);
      }
      setVisitorId(storedVisitorId);
    }
  }, []);

  // Fetch the first page once auth is checked
  useEffect(() => {
    if (authLoading || !visitorId) return; // Wait for auth and visitorId

    const fetchInitial = async () => {
      setLoading(true);
      setError(null);
      try {
        const userIdParam = userId ? `&userId=${userId}` : '';
        const visitorIdParam = !userId ? `&visitorId=${visitorId}` : '';

        const res = await fetch(`/api/community-logos?limit=16${userIdParam}${visitorIdParam}`, { cache: 'no-store' });
        const data: ApiResponse = await res.json();
        setLogos(data.logos || []);
        setNextCursor(data.nextCursor || null);
        setHasMore(!!data.nextCursor);
        logger.log("Logos:", data.logos);
      } catch (err) {
        logger.log("Error fetching initial logos:", err);
        setError("Failed to load community logos.");
      } finally {
        setLoading(false);
      }
    };
    fetchInitial();
  }, [authLoading, isAdmin, visitorId, userId]); // Re-fetch when admin status or visitorId changes

  const handleShowMore = async () => {
    if (!nextCursor) return;
    setLoading(true);
    setError(null);
    try {
      const userIdParam = userId ? `&userId=${userId}` : '';
      const visitorIdParam = !userId && visitorId ? `&visitorId=${visitorId}` : '';

      const res = await fetch(`/api/community-logos?limit=16&cursor=${nextCursor}${userIdParam}${visitorIdParam}`, { cache: 'no-store' });
      const data: ApiResponse = await res.json();
      setLogos(prev => [...prev, ...(data.logos || [])]);
      setNextCursor(data.nextCursor || null);
      setHasMore(!!data.nextCursor);
    } catch (err) {
      setError("Failed to load more community logos.");
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (logoId: string) => {
    // Allow likes without login - use visitorId for anonymous users
    if (!userId && !visitorId) {
      return;
    }

    setLikingIds(prev => new Set(prev).add(logoId));

    try {
      const res = await fetch('/api/community-logos/like', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ logoId, visitorId: !userId ? visitorId : undefined }),
      });

      if (res.ok) {
        const data = await res.json();
        setLogos(prev => prev.map(logo =>
          logo.id === logoId
            ? { ...logo, likes: data.likes, hasLiked: data.liked }
            : logo
        ));
      }
    } catch (err) {
      logger.error("Error liking logo:", err);
    } finally {
      setLikingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(logoId);
        return newSet;
      });
    }
  };

  const handleFeature = async (logoId: string) => {
    if (!isAdmin) return;

    setFeaturingIds(prev => new Set(prev).add(logoId));

    try {
      const res = await fetch('/api/community-logos/feature', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ logoId }),
      });

      if (res.ok) {
        const data = await res.json();
        setLogos(prev => prev.map(logo =>
          logo.id === logoId
            ? { ...logo, isFeatured: data.isFeatured }
            : logo
        ));
      }
    } catch (err) {
      logger.error("Error featuring logo:", err);
    } finally {
      setFeaturingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(logoId);
        return newSet;
      });
    }
  };

  const handleHide = async (logoId: string) => {
    if (!isAdmin) return;

    setHidingIds(prev => new Set(prev).add(logoId));

    try {
      const res = await fetch('/api/community-logos/hide', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ logoId }),
      });

      if (res.ok) {
        const data = await res.json();
        setLogos(prev => prev.map(logo =>
          logo.id === logoId
            ? { ...logo, isHidden: data.isHidden }
            : logo
        ));
      }
    } catch (err) {
      logger.error("Error hiding logo:", err);
    } finally {
      setHidingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(logoId);
        return newSet;
      });
    }
  };

  return (
    <>
      {error && (
        <p role="status" className="mb-6 rounded-md border border-danger/40 bg-danger-soft px-4 py-3 text-small text-danger">
          {error}
        </p>
      )}
      {loading && logos.length === 0 ? (
        <LogoGridSkeleton />
      ) : (
        <div className="grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-4 md:gap-x-6 md:gap-y-10">
          {logos.map((logo) => (
            <div key={logo.id} className="group relative">
              {/* Badges - top left */}
              <div className="absolute top-2 left-2 z-10 flex flex-col gap-1">
                {/* Featured badge */}
                {logo.isFeatured && (
                  <div className="flex items-center gap-1 rounded-full bg-ink px-2 py-0.5 text-micro font-medium text-ink-inverse">
                    <Star className="size-2.5 fill-current" />
                    Featured
                  </div>
                )}
                {/* Hidden badge - only visible to admins */}
                {isAdmin && logo.isHidden && (
                  <div className="flex items-center gap-1 rounded-full bg-danger px-2 py-0.5 text-micro font-medium text-danger-foreground">
                    <EyeOff className="size-2.5" />
                    Hidden
                  </div>
                )}
              </div>

              {/* Admin controls - top right */}
              {isAdmin && (
                <div className="absolute top-2 right-2 z-10 flex gap-1">
                  {/* Hide toggle */}
                  <button
                    onClick={() => handleHide(logo.id)}
                    disabled={hidingIds.has(logo.id)}
                    className={`rounded-md p-1 transition-colors duration-fast ease-out ${
                      logo.isHidden
                        ? 'bg-danger-soft text-danger'
                        : 'text-ink-subtle hover:text-ink'
                    } ${hidingIds.has(logo.id) ? 'cursor-not-allowed opacity-50' : ''}`}
                    title={logo.isHidden ? 'Unhide from community' : 'Hide from community'}
                  >
                    <EyeOff className={`size-4 ${logo.isHidden ? 'fill-current' : ''}`} />
                  </button>
                  {/* Feature toggle */}
                  <button
                    onClick={() => handleFeature(logo.id)}
                    disabled={featuringIds.has(logo.id)}
                    className={`rounded-md p-1 transition-colors duration-fast ease-out ${
                      logo.isFeatured
                        ? 'text-ink'
                        : 'text-ink-subtle hover:text-ink'
                    } ${featuringIds.has(logo.id) ? 'cursor-not-allowed opacity-50' : ''}`}
                    title={logo.isFeatured ? 'Remove from featured' : 'Mark as featured'}
                  >
                    <Star className={`size-4 ${logo.isFeatured ? 'fill-current' : ''}`} />
                  </button>
                </div>
              )}

              {/* Logo image */}
              <div className="relative aspect-square overflow-hidden rounded-lg border border-line bg-artboard transition-[border-color] duration-fast ease-out group-hover:border-ink">
                <Image
                  src={logo.imageUrl}
                  alt={logo.brandName ? `${logo.brandName} logo` : "Community logo"}
                  fill
                  sizes="(max-width: 768px) 45vw, 22vw"
                  className="pointer-events-none object-contain p-4"
                  unoptimized
                />

                {/* Like control, floating on the artwork */}
                <button
                  onClick={() => handleLike(logo.id)}
                  disabled={likingIds.has(logo.id)}
                  className={`absolute right-2 bottom-2 z-10 inline-flex h-8 items-center gap-1.5 rounded-full border px-2.5 backdrop-blur-sm transition-[color,background-color,border-color,transform] duration-fast ease-out active:scale-95 ${
                    logo.hasLiked
                      ? "border-signal/40 bg-signal-soft text-signal"
                      : "border-line bg-paper/85 text-ink-subtle hover:border-signal/40 hover:text-signal"
                  } ${likingIds.has(logo.id) ? "cursor-not-allowed opacity-50" : ""}`}
                  title={logo.hasLiked ? "Unlike" : "Like"}
                  aria-label={
                    logo.hasLiked
                      ? `Unlike this logo, ${logo.likes} likes`
                      : `Like this logo, ${logo.likes} likes`
                  }
                  aria-pressed={logo.hasLiked}
                >
                  <Heart
                    className={`size-3.5 transition-transform duration-fast ease-out ${
                      logo.hasLiked ? "scale-110 fill-current" : ""
                    }`}
                    aria-hidden
                  />
                  <span className="text-caption font-medium tabular-nums">{logo.likes}</span>
                </button>
              </div>

              {/* Author */}
              <div className="flex items-center gap-2.5 pt-4">
                <AuthorAvatar
                  name={logo.authorName || "Anonymous"}
                  photoURL={logo.authorPhotoURL}
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-small font-medium text-ink">
                    {logo.brandName || "Untitled mark"}
                  </p>
                  <p className="type-eyebrow truncate text-ink-subtle">
                    by {logo.authorName || "Anonymous"}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {hasMore && !hideShowMoreButton && (
        <div className="mt-12 flex justify-center">
          <button
            onClick={handleShowMore}
            disabled={loading}
            className="inline-flex h-11 items-center rounded-full border border-line px-6 text-small text-ink-muted transition-colors duration-fast ease-out hover:border-ink hover:text-ink disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? "Loading" : "Load more"}
          </button>
        </div>
      )}
    </>
  );
}
